/**
 * profileWorkItemSlice.js
 *
 * Manages state for:
 *  - ProfileWorkItem list (paginated, filtered)
 *  - Measurement list (for picker in the work item modal)
 *  - Modal open/close for create, edit, delete
 *  - Operation states: creating, updating, deleting, toggling active
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getWorkItemsApi,
    getWorkItemApi,
    createWorkItemApi,
    updateWorkItemApi,
    activateWorkItemApi,
    deactivateWorkItemApi,
    deleteWorkItemApi,
    getMeasurementsApi,
    createMeasurementApi,
    updateMeasurementApi,
    activateMeasurementApi,
    deactivateMeasurementApi,
    deleteMeasurementApi,
} from '../../services/profileWorkItemApi';
import axiosInstance from '../../services/axiosInstance';

// ============================================================
// Helpers
// ============================================================

const extractMessage = (error, fallback) =>
    error.response?.data?.message || error.response?.data?.error || fallback;

// ============================================================
// Async Thunks — Work Items
// ============================================================

export const fetchWorkItems = createAsyncThunk(
    'profileWorkItems/fetchWorkItems',
    async ({ page = 0, size = 10, activeOnly = false } = {}, { rejectWithValue }) => {
        try {
            return await getWorkItemsApi({ page, size, activeOnly });
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to fetch work items'));
        }
    }
);

export const fetchWorkItem = createAsyncThunk(
    'profileWorkItems/fetchWorkItem',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getWorkItemApi(uuid);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to fetch work item'));
        }
    }
);

export const createWorkItem = createAsyncThunk(
    'profileWorkItems/createWorkItem',
    async (data, { rejectWithValue }) => {
        try {
            return await createWorkItemApi(data);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to create work item'));
        }
    }
);

export const updateWorkItem = createAsyncThunk(
    'profileWorkItems/updateWorkItem',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateWorkItemApi(uuid, data);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to update work item'));
        }
    }
);

export const toggleWorkItemActive = createAsyncThunk(
    'profileWorkItems/toggleActive',
    async ({ uuid, active }, { rejectWithValue }) => {
        try {
            return active ? await activateWorkItemApi(uuid) : await deactivateWorkItemApi(uuid);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to update work item status'));
        }
    }
);

export const deleteWorkItem = createAsyncThunk(
    'profileWorkItems/deleteWorkItem',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteWorkItemApi(uuid);
            return uuid;
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to delete work item'));
        }
    }
);

export const provisionDefaultWorkItems = createAsyncThunk(
    'profileWorkItems/provisionDefaults',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post('/profile-work-items/provision-defaults');
            return res.data; // { provisioned, alreadyHadItems }
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to add starter work items'));
        }
    }
);

// ============================================================
// Async Thunks — Measurements
// ============================================================

export const fetchMeasurements = createAsyncThunk(
    'profileWorkItems/fetchMeasurements',
    async (params = {}, { rejectWithValue }) => {
        try {
            return await getMeasurementsApi(params);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to fetch measurements'));
        }
    }
);

export const createMeasurement = createAsyncThunk(
    'profileWorkItems/createMeasurement',
    async (data, { rejectWithValue }) => {
        try {
            return await createMeasurementApi(data);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to create measurement'));
        }
    }
);

export const updateMeasurement = createAsyncThunk(
    'profileWorkItems/updateMeasurement',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateMeasurementApi(uuid, data);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to update measurement'));
        }
    }
);

export const toggleMeasurementActive = createAsyncThunk(
    'profileWorkItems/toggleMeasurementActive',
    async ({ uuid, active }, { rejectWithValue }) => {
        try {
            return active ? await activateMeasurementApi(uuid) : await deactivateMeasurementApi(uuid);
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to update measurement status'));
        }
    }
);

export const deleteMeasurement = createAsyncThunk(
    'profileWorkItems/deleteMeasurement',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteMeasurementApi(uuid);
            return uuid;
        } catch (e) {
            return rejectWithValue(extractMessage(e, 'Failed to delete measurement'));
        }
    }
);

// ============================================================
// Initial State
// ============================================================

const initialState = {
    // Work item list
    workItems: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 0,
        size: 12,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
    },
    filters: {
        activeOnly: false,
    },

    // Measurements list (used in pickers + the Measurements tab)
    measurements: [],
    measurementsPagination: {
        page: 0,
        size: 100,
        totalElements: 0,
        totalPages: 0,
    },
    measurementsFilter: {
        activeOnly: false,
    },
    isLoadingMeasurements: false,

    // Selected item (edit modal)
    selectedWorkItem: null,
    selectedMeasurement: null,

    // Modal states
    isWorkItemModalOpen: false,      // create or edit work item
    isDeleteWorkItemModalOpen: false,
    workItemToDelete: null,

    isMeasurementModalOpen: false,   // create or edit measurement
    isDeleteMeasurementModalOpen: false,
    measurementToDelete: null,

    // Tabs: 'workItems' | 'measurements'
    activeTab: 'workItems',

    // Operation states
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isTogglingActive: false,
    isRefreshing: false,
    isProvisioning: false,
    operationError: null,
    operationSuccess: null,
};

// ============================================================
// Slice
// ============================================================

const profileWorkItemSlice = createSlice({
    name: 'profileWorkItems',
    initialState,
    reducers: {
        // Tab
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },

        // Work item modals
        openCreateWorkItemModal: (state) => {
            state.isWorkItemModalOpen = true;
            state.selectedWorkItem = null;
            state.operationError = null;
        },
        openEditWorkItemModal: (state, action) => {
            state.isWorkItemModalOpen = true;
            state.selectedWorkItem = action.payload;
            state.operationError = null;
        },
        closeWorkItemModal: (state) => {
            state.isWorkItemModalOpen = false;
            state.selectedWorkItem = null;
            state.operationError = null;
        },
        openDeleteWorkItemModal: (state, action) => {
            state.isDeleteWorkItemModalOpen = true;
            state.workItemToDelete = action.payload;
            state.operationError = null;
        },
        closeDeleteWorkItemModal: (state) => {
            state.isDeleteWorkItemModalOpen = false;
            state.workItemToDelete = null;
            state.operationError = null;
        },

        // Measurement modals
        openCreateMeasurementModal: (state) => {
            state.isMeasurementModalOpen = true;
            state.selectedMeasurement = null;
            state.operationError = null;
        },
        openEditMeasurementModal: (state, action) => {
            state.isMeasurementModalOpen = true;
            state.selectedMeasurement = action.payload;
            state.operationError = null;
        },
        closeMeasurementModal: (state) => {
            state.isMeasurementModalOpen = false;
            state.selectedMeasurement = null;
            state.operationError = null;
        },
        openDeleteMeasurementModal: (state, action) => {
            state.isDeleteMeasurementModalOpen = true;
            state.measurementToDelete = action.payload;
            state.operationError = null;
        },
        closeDeleteMeasurementModal: (state) => {
            state.isDeleteMeasurementModalOpen = false;
            state.measurementToDelete = null;
            state.operationError = null;
        },

        // Filters & pagination
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
            state.pagination.page = 0;
        },
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setMeasurementsFilter: (state, action) => {
            state.measurementsFilter = { ...state.measurementsFilter, ...action.payload };
            state.measurementsPagination.page = 0;
        },
        setMeasurementsPage: (state, action) => {
            state.measurementsPagination.page = action.payload;
        },

        // Clear
        clearOperationSuccess: (state) => { state.operationSuccess = null; },
        clearOperationError: (state) => { state.operationError = null; },
    },

    extraReducers: (builder) => {

        // ---- Fetch Work Items ----
        builder
            .addCase(fetchWorkItems.pending, (state) => {
                if (!state.isRefreshing) state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchWorkItems.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isRefreshing = false;
                const p = action.payload;
                // Micronaut Page shape: { content, pageable: { number, size }, totalSize }
                const number = p.pageable?.number ?? p.pageable?.pageNumber ?? p.page ?? 0;
                const size = p.pageable?.size ?? p.pageable?.pageSize ?? p.size ?? 10;
                const total = p.totalSize ?? p.totalElements ?? (p.content?.length ?? 0);
                const totalPages = size > 0 ? Math.ceil(total / size) : 0;
                state.workItems = p.content ?? [];
                state.pagination = {
                    page: number,
                    size,
                    totalElements: total,
                    totalPages,
                    hasNext: number + 1 < totalPages,
                    hasPrevious: number > 0,
                };
            })
            .addCase(fetchWorkItems.rejected, (state, action) => {
                state.isLoading = false;
                state.isRefreshing = false;
                state.error = action.payload;
            });

        // ---- Create Work Item ----
        builder
            .addCase(createWorkItem.pending, (state) => {
                state.isCreating = true;
                state.operationError = null;
            })
            .addCase(createWorkItem.fulfilled, (state, action) => {
                state.isCreating = false;
                state.isWorkItemModalOpen = false;
                state.isRefreshing = true;
                state.operationSuccess = `Work item "${action.payload.action}" created successfully`;
            })
            .addCase(createWorkItem.rejected, (state, action) => {
                state.isCreating = false;
                state.operationError = action.payload;
            });

        // ---- Update Work Item ----
        builder
            .addCase(updateWorkItem.pending, (state) => {
                state.isUpdating = true;
                state.operationError = null;
            })
            .addCase(updateWorkItem.fulfilled, (state, action) => {
                state.isUpdating = false;
                state.isWorkItemModalOpen = false;
                state.selectedWorkItem = null;
                state.operationSuccess = `Work item "${action.payload.action}" updated successfully`;
                const idx = state.workItems.findIndex(w => w.uuid === action.payload.uuid);
                if (idx !== -1) state.workItems[idx] = action.payload;
            })
            .addCase(updateWorkItem.rejected, (state, action) => {
                state.isUpdating = false;
                state.operationError = action.payload;
            });

        // ---- Toggle Work Item Active ----
        builder
            .addCase(toggleWorkItemActive.pending, (state) => { state.isTogglingActive = true; })
            .addCase(toggleWorkItemActive.fulfilled, (state, action) => {
                state.isTogglingActive = false;
                const idx = state.workItems.findIndex(w => w.uuid === action.payload.uuid);
                if (idx !== -1) state.workItems[idx] = action.payload;
                state.operationSuccess = `Work item ${action.payload.active ? 'activated' : 'deactivated'}`;
            })
            .addCase(toggleWorkItemActive.rejected, (state, action) => {
                state.isTogglingActive = false;
                state.operationError = action.payload;
            });

        // ---- Delete Work Item ----
        builder
            .addCase(deleteWorkItem.pending, (state) => {
                state.isDeleting = true;
                state.operationError = null;
            })
            .addCase(deleteWorkItem.fulfilled, (state, action) => {
                state.isDeleting = false;
                state.isDeleteWorkItemModalOpen = false;
                state.workItemToDelete = null;
                state.workItems = state.workItems.filter(w => w.uuid !== action.payload);
                state.pagination.totalElements = Math.max(0, state.pagination.totalElements - 1);
                state.operationSuccess = 'Work item deleted';
            })
            .addCase(deleteWorkItem.rejected, (state, action) => {
                state.isDeleting = false;
                state.operationError = action.payload;
            });

        // ---- Provision Default Work Items ----
        builder
            .addCase(provisionDefaultWorkItems.pending, (state) => {
                state.isProvisioning = true;
                state.operationError = null;
            })
            .addCase(provisionDefaultWorkItems.fulfilled, (state, action) => {
                state.isProvisioning = false;
                const { provisioned, alreadyHadItems } = action.payload || {};
                if (alreadyHadItems) {
                    state.operationSuccess = 'Work items already set up';
                } else {
                    state.operationSuccess = `Added ${provisioned} starter work item${provisioned === 1 ? '' : 's'}`;
                    // Trigger the page's refresh effect to reload the list
                    state.isRefreshing = true;
                }
            })
            .addCase(provisionDefaultWorkItems.rejected, (state, action) => {
                state.isProvisioning = false;
                state.operationError = action.payload;
            });

        // ---- Fetch Measurements ----
        builder
            .addCase(fetchMeasurements.pending, (state) => { state.isLoadingMeasurements = true; })
            .addCase(fetchMeasurements.fulfilled, (state, action) => {
                state.isLoadingMeasurements = false;
                const p = action.payload;
                state.measurements = p.content;
                state.measurementsPagination = {
                    page: p.pageable?.pageNumber ?? p.page ?? 0,
                    size: p.pageable?.pageSize ?? p.size ?? 100,
                    totalElements: p.totalElements,
                    totalPages: p.totalPages,
                };
            })
            .addCase(fetchMeasurements.rejected, (state, action) => {
                state.isLoadingMeasurements = false;
                state.operationError = action.payload;
            });

        // ---- Create Measurement ----
        builder
            .addCase(createMeasurement.pending, (state) => {
                state.isCreating = true;
                state.operationError = null;
            })
            .addCase(createMeasurement.fulfilled, (state, action) => {
                state.isCreating = false;
                state.isMeasurementModalOpen = false;
                state.measurements = [...state.measurements, action.payload];
                state.operationSuccess = `Measurement "${action.payload.name}" created successfully`;
            })
            .addCase(createMeasurement.rejected, (state, action) => {
                state.isCreating = false;
                state.operationError = action.payload;
            });

        // ---- Update Measurement ----
        builder
            .addCase(updateMeasurement.pending, (state) => {
                state.isUpdating = true;
                state.operationError = null;
            })
            .addCase(updateMeasurement.fulfilled, (state, action) => {
                state.isUpdating = false;
                state.isMeasurementModalOpen = false;
                state.selectedMeasurement = null;
                const idx = state.measurements.findIndex(m => m.uuid === action.payload.uuid);
                if (idx !== -1) state.measurements[idx] = action.payload;
                state.operationSuccess = `Measurement "${action.payload.name}" updated successfully`;
            })
            .addCase(updateMeasurement.rejected, (state, action) => {
                state.isUpdating = false;
                state.operationError = action.payload;
            });

        // ---- Toggle Measurement Active ----
        builder
            .addCase(toggleMeasurementActive.fulfilled, (state, action) => {
                const idx = state.measurements.findIndex(m => m.uuid === action.payload.uuid);
                if (idx !== -1) state.measurements[idx] = action.payload;
                state.operationSuccess = `Measurement ${action.payload.active ? 'activated' : 'deactivated'}`;
            })
            .addCase(toggleMeasurementActive.rejected, (state, action) => {
                state.operationError = action.payload;
            });

        // ---- Delete Measurement ----
        builder
            .addCase(deleteMeasurement.pending, (state) => {
                state.isDeleting = true;
                state.operationError = null;
            })
            .addCase(deleteMeasurement.fulfilled, (state, action) => {
                state.isDeleting = false;
                state.isDeleteMeasurementModalOpen = false;
                state.measurementToDelete = null;
                state.measurements = state.measurements.filter(m => m.uuid !== action.payload);
                state.operationSuccess = 'Measurement deleted';
            })
            .addCase(deleteMeasurement.rejected, (state, action) => {
                state.isDeleting = false;
                state.operationError = action.payload;
            });
    },
});

// ============================================================
// Actions
// ============================================================

export const {
    setActiveTab,
    openCreateWorkItemModal, openEditWorkItemModal, closeWorkItemModal,
    openDeleteWorkItemModal, closeDeleteWorkItemModal,
    openCreateMeasurementModal, openEditMeasurementModal, closeMeasurementModal,
    openDeleteMeasurementModal, closeDeleteMeasurementModal,
    setFilters, setPage,
    setMeasurementsFilter, setMeasurementsPage,
    clearOperationSuccess, clearOperationError,
} = profileWorkItemSlice.actions;

// ============================================================
// Selectors
// ============================================================

export const selectWorkItems            = (s) => s.profileWorkItems.workItems;
export const selectIsLoading            = (s) => s.profileWorkItems.isLoading;
export const selectError                = (s) => s.profileWorkItems.error;
export const selectPagination           = (s) => s.profileWorkItems.pagination;
export const selectFilters              = (s) => s.profileWorkItems.filters;

export const selectMeasurements         = (s) => s.profileWorkItems.measurements;
export const selectIsLoadingMeasurements= (s) => s.profileWorkItems.isLoadingMeasurements;
export const selectMeasurementsPagination=(s) => s.profileWorkItems.measurementsPagination;
export const selectMeasurementsFilter   = (s) => s.profileWorkItems.measurementsFilter;

export const selectSelectedWorkItem     = (s) => s.profileWorkItems.selectedWorkItem;
export const selectSelectedMeasurement  = (s) => s.profileWorkItems.selectedMeasurement;

export const selectActiveTab            = (s) => s.profileWorkItems.activeTab;

export const selectIsWorkItemModalOpen        = (s) => s.profileWorkItems.isWorkItemModalOpen;
export const selectIsDeleteWorkItemModalOpen  = (s) => s.profileWorkItems.isDeleteWorkItemModalOpen;
export const selectWorkItemToDelete           = (s) => s.profileWorkItems.workItemToDelete;

export const selectIsMeasurementModalOpen       = (s) => s.profileWorkItems.isMeasurementModalOpen;
export const selectIsDeleteMeasurementModalOpen = (s) => s.profileWorkItems.isDeleteMeasurementModalOpen;
export const selectMeasurementToDelete          = (s) => s.profileWorkItems.measurementToDelete;

export const selectIsCreating           = (s) => s.profileWorkItems.isCreating;
export const selectIsUpdating           = (s) => s.profileWorkItems.isUpdating;
export const selectIsDeleting           = (s) => s.profileWorkItems.isDeleting;
export const selectIsRefreshing         = (s) => s.profileWorkItems.isRefreshing;
export const selectIsProvisioning       = (s) => s.profileWorkItems.isProvisioning;
export const selectOperationError       = (s) => s.profileWorkItems.operationError;
export const selectOperationSuccess     = (s) => s.profileWorkItems.operationSuccess;

export default profileWorkItemSlice.reducer;